import { usePreferredSessionStorage } from "@/composables/usePreferedSessionStorage";
import router from "@/router";
import { CustomColumn, Layout, Order, OrderItem } from "@/types";
import { getSocket } from "@/utils/http-common";
import { safeLocaleCompare } from "@/utils/string.utils";
import { useLocalStorage, useStorage } from "@vueuse/core";
import dayjs from "dayjs";
import { defineStore } from "pinia";
import { Socket } from "socket.io-client";
import { onUnmounted, ref } from "vue";
import { getIncomingOrderItems, getOrderItemsWithInfo } from "../services/OrderItems.service";
import { getOrders } from "../services/OrdersService";
import { useAnalyticsStore } from "./analytics.store";
import { useWorkflowsStore } from "./workflows.store";
import { DataTableFilterMeta } from "primevue";

export const orderColumns: CustomColumn[] = [
  { header: "Order Code", field: "order.code", sortable: true, frozen: false, class: "", filter: true, showFilterMenu: true, entity: "Order" },
  { header: "Order Name", field: "order.name", sortable: true, frozen: false, class: "max-w-80", filter: true, showFilterMenu: true, entity: "Order" },
  { header: "Order Type", field: "orderType.name", sortable: true, frozen: false, class: "max-w-80", filter: true, showFilterMenu: true, entity: "Order" },
  { header: "Delivery Date", field: "order.dueDate", sortable: true, frozen: false, class: "min-w-52", filter: true, showFilterMenu: true, entity: "Order" },
  { header: "Material", field: "material.name", sortable: true, frozen: false, class: "", filter: true, entity: "OrderItem" },
  { header: "File Name", field: "currentFile.originalName", sortable: true, frozen: false, filter: true, showFilterMenu: true, entity: "OrderItem" },
  { header: "Object Size", field: "currentFile.metadata.ImageInfo", sortable: true, frozen: false, class: "min-w-36", filter: false, showFilterMenu: false, entity: "OrderItem" },
  { header: "Description", field: "description", sortable: true, frozen: false, class: "min-w-max whitespace-nowrap", filter: true, showFilterMenu: true, entity: "OrderItem" },
  { header: "Quantity", field: "quantity", sortable: true, frozen: false, filter: false, showFilterMenu: true, entity: "OrderItem" },
  { header: "Width", field: "width", sortable: true, frozen: false, class: "", filter: false, showFilterMenu: true, entity: "OrderItem" },
  { header: "Length", field: "length", sortable: true, frozen: false, class: "", filter: false, showFilterMenu: true, entity: "OrderItem" },
  { header: "Batch", field: "batch.batchNumber", sortable: true, frozen: false, class: "", filter: true, entity: "OrderItem" },
  { header: "Printer", field: "printer.name", sortable: true, frozen: false, class: "min-w-44", filter: true, filterField: "printerId", showFilterMenu: true, entity: "OrderItem" },
  { header: "Scheme", field: "scheme.name", sortable: true, frozen: false, class: "min-w-36", filter: true, entity: "OrderItem" },
  { header: "Type", field: "type", sortable: true, frozen: false, showFilterMenu: true, filter: true, isJobColumn: true, entity: "PrintJob" },
  { header: "Layout", field: "layout.name", sortable: true, frozen: false, class: "min-w-36", filter: true, entity: "OrderItem" },
  { header: "Action", field: "action", sortable: true, frozen: false, filter: true, showFilterMenu: false, entity: "OrderItem" },
  { header: "Customer", field: "order.customer.name", sortable: true, frozen: false, class: "min-w-44", filter: true, entity: "Order" },
  { header: "Status", field: "status", sortable: true, frozen: false, class: "", filter: true, entity: "OrderItem" },
  { header: "Percentage", field: "percentage", sortable: true, frozen: false, isJobColumn: true, entity: "PrintJob" },
  { header: "Printed Length", field: "printedLength", sortable: true, frozen: false, isJobColumn: true, entity: "PrintJob" },
  { header: "Ink Consumption", field: "inkConsumption", sortable: false, frozen: false, isJobColumn: true, entity: "PrintJob" },
  { header: "Abort Date", field: "abortDate", sortable: false, frozen: false, filter: true, showFilterMenu: true, isJobColumn: true, entity: "PrintJob" },
  { header: "End Date", field: "endDate", sortable: false, frozen: false, filter: true, showFilterMenu: true, isJobColumn: true, entity: "PrintJob" },
  { header: "Archive Date", field: "archivedAt", sortable: false, frozen: false, filter: true, showFilterMenu: true, isJobColumn: true, entity: "PrintJob" },
  { header: "Creation Date", field: "createdAt", sortable: true, frozen: false, filter: true, showFilterMenu: true, class: "min-w-44", entity: "OrderItem" },
  { header: "Update Date", field: "updatedAt", sortable: true, frozen: false, filter: true, showFilterMenu: true, class: "min-w-44", entity: "OrderItem" },
  { header: "Order Comments", field: "order.comments", sortable: true, frozen: false, filter: true, showFilterMenu: true, entity: "Order" },
  { header: "Item Comments", field: "comments", sortable: true, frozen: false, filter: true, showFilterMenu: true, entity: "OrderItem" },
  { header: "Creator", field: "order.createdBy.name", sortable: true, frozen: false, filter: true, showFilterMenu: true, filterField: "order.createdById", entity: "Order" },
];

const sortingMethods = [
  { label: "Order Code", value: "order.code" },
  { label: "Order Name", value: "order.name" },
  { label: "Delivery date", value: "order.dueDate" },
  { label: "Description", value: "description" },
  { label: "Quantity", value: "quantity" },
  { label: "Width", value: "width" },
  { label: "Length", value: "length" },
  { label: "Batch", value: "batch" },
  { label: "Status", value: "status" },
  { label: "Creation date", value: "createdAt" },
  { label: "Update date", value: "updatedAt" },
  { label: "Material", value: "material.name" },
  { label: "Customer", value: "order.customer.name" },
];

const ITEMS_PER_PAGE = 25;
export const useOrdersStore = defineStore("orders", {
  state: () => ({
    totalOrderItems: new Map<number, number>(),
    orders: ref([] as Order[]),
    filters: ref<DataTableFilterMeta>({}),
    viewType: useLocalStorage("viewType", "full"),
    selectedItems: [] as OrderItem[],
    selectedOrderItem: {} as OrderItem,
    showExpired: usePreferredSessionStorage<boolean>("showExpired", true),
    showArchived: usePreferredSessionStorage<boolean>("showArchived", false),
    selectedItemsForTable: [] as OrderItem[],
    selectedItemsForPdf: [] as OrderItem[],
    pdfHeaderInfo: null as { name: string; totalItems: number; totalLength: number; type: string; entityId: string | number } | null,
    isEditing: false,
    visibleAddNewOrderDialog: false,
    selectedOrder: {} as Order,
    searchTerm: "",
    incomingOrderItems: [] as OrderItem[],
    orderIdFromUrl: 0,
    orderItems: [] as OrderItem[],
    columnOrder: usePreferredSessionStorage("columnOrder", [] as string[]),
    event: null as any,
    sortingMethods: sortingMethods,
    selectedSortingMethod: useStorage<{ workflowId?: number; value?: string; label?: string }[]>("sortingMethod", [], localStorage, { mergeDefaults: true }),
    selectedSortingOrder: useStorage<{ workflowId?: number; order?: string }[]>("sortingOrder", [], localStorage),
    socket: null as Socket | null,
    orderItemsByStage: new Map<number, OrderItem>(),
    returnUrl: undefined as string | undefined,
    lastSelectedLayout: useStorage<Layout | null>("lastSelectedLayout", null, localStorage, {
      mergeDefaults: true,
    }),
  }),
  getters: {
    selectedOrderColumns(): CustomColumn[] {
      const allCols = [...orderColumns];
      return this.columnOrder.map((field) => allCols.find((col) => col.field === field)).filter(Boolean) as CustomColumn[];
    },
    itemsPerPage(): number {
      return ITEMS_PER_PAGE;
    },
    allSelected(): boolean {
      if (!this.selectedItems.length || !this.selectedOrder.orderItems.filter((item: OrderItem) => !isAccepted(item)).length) return false;
      if (this.selectedItems.length != this.selectedOrder.orderItems.filter((item: OrderItem) => !isAccepted(item)).length) return false;
      const items = this.selectedOrder.orderItems.filter((item: OrderItem) => !isAccepted(item));
      return items.every((item: OrderItem) => this.selectedItems.includes(item)) && this.selectedItems.every((item: OrderItem) => items.includes(item));
    },
    incomingOrderItemsLength(): number {
      return this.incomingOrderItems.length;
    },
    incomingItems(): OrderItem[] {
      return this.incomingOrderItems;
    },
    orderItemsFilteredByDate(): OrderItem[] {
      if (!this.orderItems.length) return [];
      const analyticsStore = useAnalyticsStore();

      const [startDate, endDate] = analyticsStore.selectedDateRange;
      const orderItems = this.orderItems.filter((item) => {
        const creationDate = dayjs(item.createdAt);
        return creationDate.isAfter(dayjs(startDate)) && creationDate.isBefore(dayjs(endDate));
      });
      return orderItems;
    },
    orderItemsByMaterialType(): Map<string, OrderItem[]> {
      if (!this.orderItemsFilteredByDate.length) return new Map<string, OrderItem[]>();
      const orderItemsByMaterialType = this.orderItemsFilteredByDate.reduce((acc, curr: OrderItem) => {
        if (!curr.material?.materialType?.name) {
          acc.set("No Material", [...(acc.get("No Material") ?? []), curr]);
        } else {
          acc.set(curr.material.materialType.name, [...(acc.get(curr.material.materialType.name) ?? []), curr]);
        }
        return acc;
      }, new Map<string, OrderItem[]>());
      return orderItemsByMaterialType;
    },
    orderItemsFiltered(): OrderItem[] {
      // Search is now handled server-side, just filter out attachments
      return this.orderItems.filter((item) => !item.isAttachment);
    }, // Combina ambas: columnas seleccionadas en el orden guardado
    orderedSelectedColumns(): typeof orderColumns {
      return this.selectedOrderColumns;
    },
  },

  actions: {
    // Helper methods to properly trigger reactivity for Map mutations
    setOrderItemByStage(itemId: number, item: OrderItem) {
      this.orderItemsByStage.set(itemId, item);
      // Trigger reactivity by reassigning the Map
      this.orderItemsByStage = new Map(this.orderItemsByStage);
    },
    deleteOrderItemByStage(itemId: number) {
      this.orderItemsByStage.delete(itemId);
      // Trigger reactivity by reassigning the Map
      this.orderItemsByStage = new Map(this.orderItemsByStage);
    },
    updateOrderItem(orderItem: OrderItem) {
      const index = this.orderItems.findIndex((item) => item.id === orderItem.id);
      if (index === -1) {
        this.orderItems.push(orderItem);
        return;
      }
      this.orderItems[index] = orderItem;
    },
    fetchIncomingOrdersPeriodically() {
      const intervalId = setInterval(() => {
        this.fetchIncomingOrderItems();
      }, 30 * 1000); // Fetch every 30 seconds

      onUnmounted(() => {
        clearInterval(intervalId); // Clear the interval when the component is unmounted
      });
    },
    async getOrders() {
      this.orders = await getOrders();
    },
    async fetchAllOrderItems(dateFrom?: Date, dateTo?: Date) {
      this.orderItems = [];

      const batchSize = 2;
      const limit = 500;
      let filters = "";
      if (dateFrom && dateTo) {
        const from = dayjs(dateFrom).toISOString();
        const to = dayjs(dateTo).toISOString();

        filters = `createdAt__between=${from},${to}`;
      }
      // First call to get total count
      const firstPageData = await getOrderItemsWithInfo(undefined, limit, 1, "createdAt", -1, filters, "full");

      const total = firstPageData.total || 0;

      const allItems: OrderItem[] = [...firstPageData.data];
      if (total <= limit) {
        this.orderItems = allItems;
        return;
      }

      // Calculate total pages needed
      const totalPages = Math.ceil(total / limit);
      const remainingPages = Array.from({ length: totalPages - 1 }, (_, i) => i + 2);

      // Process pages in batches for parallel requests
      for (let i = 0; i < remainingPages.length; i += batchSize) {
        const batch = remainingPages.slice(i, i + batchSize);

        const batchPromises = batch.map((page) => getOrderItemsWithInfo(undefined, limit, page, "createdAt", -1, filters, "full"));

        const batchResults = await Promise.all(batchPromises);

        batchResults.forEach((result) => {
          allItems.push(...result.data);
        });
      }
      this.orderItems = allItems;
    },

    async loadMoreOrderItems(stageId: number, limit?: number, page?: number, sortField?: string, sortOrder?: 0 | 1 | -1 | null, filters?: string) {
      const workflowsStore = useWorkflowsStore();

      const stageCategory = workflowsStore.selectedWorkflow.workflowStages?.find((stage) => stage.id === stageId)?.category;

      if (!limit) {
        limit = stageCategory === "Partners" ? undefined : ITEMS_PER_PAGE;
      }

      return getOrderItemsWithInfo(stageId, limit, page, sortField, sortOrder, filters).then((res) => {
        this.orderItems.push(...res.data);
        this.totalOrderItems.set(stageId, res.total);
        res.data.forEach((item: OrderItem) => {
          this.orderItemsByStage.set(item.id!, item);
        });
        // Trigger reactivity after all items are added
        this.orderItemsByStage = new Map(this.orderItemsByStage);
        return res.data;
      });
    },
    closeAddNewOrderDialog() {
      this.visibleAddNewOrderDialog = false;
      this.isEditing = false;
      const url = this.returnUrl;
      this.returnUrl = undefined;
      if (url) {
        router.replace(url);
        return;
      }
      // Opened via /orders/:id without a stored return URL — fall back to the workflow board if possible
      if (router.currentRoute.value.params.id) {
        const workflowId = useWorkflowsStore().selectedWorkflow?.id;
        router.replace(workflowId ? `/home/orders/workflows/${workflowId}` : "/home/orders");
      }
    },
    openAddNewOrderDialog(url?: string) {
      if (url !== undefined) this.returnUrl = url;
      this.isEditing = false;
      this.visibleAddNewOrderDialog = true;
    },
    async fetchIncomingOrderItems() {
      const result = await getIncomingOrderItems();
      this.incomingOrderItems = result;
    },

    startIncomingEvents() {
      const socket = getSocket();
      this.socket = socket;

      socket.on("connect", () => {
        console.log("Socket connected for incoming order events");
      });

      socket.on("incomingOrderItem", () => {
        // Handle incoming order item
        this.fetchIncomingOrderItems();
      });

      socket.on("disconnect", () => {
        console.log("Socket disconnected from incoming order events");
      });

      socket.on("connect_error", (error) => {
        console.error("Socket connection error:", error);
      });

      socket.connect();
    },

    groupOrderItemsByField(items: OrderItem[], fieldPath: string, fieldName: string) {
      if (!items.length) return new Map<string, OrderItem[]>();
      const orderItemsByField = items.reduce((acc, item: OrderItem) => {
        const value: any = fieldPath.split(".").reduce((obj, prop) => obj && obj[prop as keyof typeof obj], item);
        if (!value) {
          acc.set("No " + fieldName, [...(acc.get("No " + fieldName) ?? []), item]);
        } else {
          const name = typeof value === "string" ? value.charAt(0).toUpperCase() + value.slice(1) : value;
          acc.set(name, [...(acc.get(name) ?? []), item]);
        }
        return acc;
      }, new Map<string, OrderItem[]>());
      return orderItemsByField;
    },

    sort(items: OrderItem[], sortingMethod: string, sortingOrder: string): OrderItem[] {
      return items.sort((a, b) => {
        switch (sortingMethod) {
          case "code":
            return safeLocaleCompare(a.order?.code, b.order?.code, sortingOrder);
          case "name":
            return safeLocaleCompare(a.order?.name, b.order?.name, sortingOrder);
          case "dueDate":
            return sortingOrder === "DESC" ? dayjs(b.order?.dueDate).diff(dayjs(a.order?.dueDate)) : dayjs(a.order?.dueDate).diff(dayjs(b.order?.dueDate));
          case "description":
            return safeLocaleCompare(a.description, b.description, sortingOrder);
          case "quantity":
            return safeLocaleCompare(a.quantity, b.quantity, sortingOrder);
          case "width":
            return safeLocaleCompare(a.width, b.width, sortingOrder);
          case "length":
            return safeLocaleCompare(a.length, b.length, sortingOrder);
          case "status":
            return safeLocaleCompare(a.status, b.status, sortingOrder);
          case "createdAt":
            return sortingOrder === "DESC" ? dayjs(b.createdAt).diff(dayjs(a.createdAt)) : dayjs(a.createdAt).diff(dayjs(b.createdAt));
          case "updatedAt":
            return sortingOrder === "DESC" ? dayjs(b.updatedAt).diff(dayjs(a.updatedAt)) : dayjs(a.updatedAt).diff(dayjs(b.updatedAt));
          case "material":
            return safeLocaleCompare(a.material?.name, b.material?.name, sortingOrder);
          case "customer":
            return safeLocaleCompare(a.order?.customer?.name, b.order?.customer?.name, sortingOrder);
          default:
            return 0;
        }
      });
    },

    addOrderItem(orderItem: OrderItem) {
      this.orderItems.push(orderItem);
    },
    deleteOrderItem(orderItem: OrderItem) {
      const index = this.orderItems.findIndex((item) => item.id === orderItem.id);
      if (index !== -1) {
        this.orderItems.splice(index, 1);
      }
    },
    reorderColumn(fromIndex: number, toIndex: number) {
      const columns = [...this.orderedSelectedColumns];
      const column = columns.splice(fromIndex, 1)[0];

      if (column) {
        columns.splice(toIndex, 0, column);
        this.columnOrder = columns.map((col) => col.field);
      }
    },

    resetColumnOrder() {
      this.columnOrder = this.selectedOrderColumns.map((col) => col.field);
    },
  },
});

export function isAccepted(item: OrderItem): boolean {
  return !!item.status && item.status.toLocaleLowerCase() !== "pending" && item.status.toLocaleLowerCase() !== "error";
}
